/* Settings → API traffic (super admins): every /api request of the panel, from GET /api/metrics/traffic
 * (server/services/apiTrafficService.js, 1-minute buckets kept 30 days). Requests by source, errors,
 * average response time, busiest endpoints and API keys. Plain SVG charts, one axis each. */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './traffic.css';

const RANGES = ['1h', '24h', '7d', '30d'];
const SOURCES = ['panel', 'api_key', 'public', 'game_logs'];
const ERRORS = ['errors_4xx', 'errors_5xx'];
const COLOR = {
  panel: 'var(--tfc-panel)', api_key: 'var(--tfc-api_key)', public: 'var(--tfc-public)', game_logs: 'var(--tfc-game_logs)',
  errors_4xx: 'var(--tfc-4xx)', errors_5xx: 'var(--tfc-5xx)',
};
const H = 180, PAD_L = 44, PAD_B = 22, PAD_T = 8;

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// axis top = 4 grid steps of 1 / 2 / 5 × 10^k (whole numbers: these are counts and ms)
function niceMax(v) {
  const raw = Math.max(v || 0, 1) / 4;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = Math.max(1, ([1, 2, 5, 10].find(s => s * p >= raw) || 10) * p);
  return step * 4;
}
const fmtNum = (n, lang) => (n == null ? '—' : Number(n).toLocaleString(lang));
const fmtShort = (n) => (n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `${Math.round(n / 1e3)}k` : n >= 1e3 ? `${+(n / 1e3).toFixed(1)}k` : String(Math.round(n)));

function timeLabel(ms, bucketMin, lang, long) {
  const d = new Date(ms);
  if (bucketMin >= 360 || (long && bucketMin >= 60)) return d.toLocaleString(lang, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  if (bucketMin >= 60) return d.toLocaleString(lang, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
}

// rect with rounded top corners only (data end), anchored to the baseline
function topRounded(x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** Chart frame: grid, y labels, x labels, hover bands + tooltip. `draw` renders the marks. */
function Plot({ series, bucketMin, max, yFmt, draw, tip, label }) {
  const { i18n } = useTranslation();
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const n = series.length;
  const plotW = Math.max(0, width - PAD_L);
  const slot = n ? plotW / n : 0;
  const y = (v) => PAD_T + (H - PAD_T - PAD_B) * (1 - v / max);
  const ticks = [0, max / 4, max / 2, (3 * max) / 4, max];
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 110))));
  return (
    <div className="tfc-plot" ref={ref} onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg viewBox={`0 0 ${width} ${H}`} height={H} role="img" aria-label={label}>
          {ticks.map((v, i) => (
            <g key={i}>
              <line className={i === 0 ? 'tfc-baseline' : 'tfc-gridline'} x1={PAD_L} x2={width} y1={y(v)} y2={y(v)} />
              <text x={PAD_L - 8} y={y(v) + 4} textAnchor="end">{yFmt(v)}</text>
            </g>
          ))}
          {series.map((b, i) => (i % every === 0 ? (
            <text key={`x${i}`} x={PAD_L + i * slot + slot / 2} y={H - 4} textAnchor="middle">{timeLabel(b.t, bucketMin, i18n.language)}</text>
          ) : null))}
          {draw({ y, slot, x0: PAD_L })}
          {series.map((b, i) => (
            <rect key={`h${i}`} className={`tfc-hit${hover === i ? ' on' : ''}`} x={PAD_L + i * slot} y={PAD_T} width={Math.max(slot, 1)} height={H - PAD_T - PAD_B}
              onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
      )}
      {hover != null && series[hover] && (
        <div className="tfc-tip" style={{ [((hover + 0.5) / n) > 0.6 ? 'right' : 'left']: ((hover + 0.5) / n) > 0.6 ? `${width - (PAD_L + hover * slot)}px` : `${PAD_L + (hover + 1) * slot + 6}px` }}>
          <div className="tfc-tip-time">{timeLabel(series[hover].t, bucketMin, i18n.language, true)}</div>
          {tip(series[hover])}
        </div>
      )}
    </div>
  );
}

function StackedColumns({ series, keys, bucketMin, names, label }) {
  const { i18n } = useTranslation();
  const max = niceMax(Math.max(0, ...series.map(b => keys.reduce((a, k) => a + (b[k] || 0), 0))));
  return (
    <Plot series={series} bucketMin={bucketMin} max={max} yFmt={fmtShort} label={label}
      draw={({ y, slot, x0 }) => {
        const gap = slot > 5 ? 2 : slot > 2.5 ? 1 : 0;
        const bw = Math.max(1, slot - gap);
        return series.map((b, i) => {
          let acc = 0;
          const top = [...keys].reverse().find(k => b[k] > 0);
          return keys.map(k => {
            const v = b[k] || 0;
            if (!v) return null;
            const y1 = y(acc + v), y0 = y(acc);
            acc += v;
            const h = Math.max(1, y0 - y1 - (k === top ? 0 : 2)); // 2px surface gap between segments
            const x = x0 + i * slot + gap / 2;
            return k === top
              ? <path key={k} d={topRounded(x, y1, bw, Math.max(1, y0 - y1), bw > 8 ? 4 : bw / 3)} fill={COLOR[k]} />
              : <rect key={k} x={x} y={y1 + (y0 - y1 - h)} width={bw} height={h} fill={COLOR[k]} />;
          });
        });
      }}
      tip={(b) => (
        <>
          {keys.map(k => (
            <div key={k} className="tfc-tip-row" style={{ '--c': COLOR[k] }}><i /><span>{names[k]}</span><b>{fmtNum(b[k], i18n.language)}</b></div>
          ))}
          <div className="tfc-tip-row total"><span>{names.total}</span><b>{fmtNum(keys.reduce((a, k) => a + (b[k] || 0), 0), i18n.language)}</b></div>
        </>
      )} />
  );
}

function LineChart({ series, field, bucketMin, unit, name, label }) {
  const { i18n } = useTranslation();
  const max = niceMax(Math.max(0, ...series.map(b => b[field] || 0)));
  return (
    <Plot series={series} bucketMin={bucketMin} max={max} yFmt={(v) => `${fmtShort(v)}${unit}`} label={label}
      draw={({ y, slot, x0 }) => {
        // a gap in the line where a bucket had no requests (no response time)
        const parts = []; let cur = [];
        series.forEach((b, i) => {
          if (b[field] == null) { if (cur.length) parts.push(cur); cur = []; return; }
          cur.push([x0 + i * slot + slot / 2, y(b[field])]);
        });
        if (cur.length) parts.push(cur);
        return parts.map((p, i) => (p.length === 1
          ? <circle key={i} className="tfc-dot" cx={p[0][0]} cy={p[0][1]} r={4} />
          : <polyline key={i} className="tfc-line" points={p.map(q => q.join(',')).join(' ')} />));
      }}
      tip={(b) => (
        <div className="tfc-tip-row" style={{ '--c': 'var(--tfc-line)' }}><i /><span>{name}</span><b>{b[field] == null ? '—' : `${fmtNum(b[field], i18n.language)}${unit}`}</b></div>
      )} />
  );
}

export default function ApiTrafficPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [range, setRange] = useState(() => { try { return RANGES.includes(localStorage.getItem('fluxbot_traffic_range')) ? localStorage.getItem('fluxbot_traffic_range') : '24h'; } catch (_) { return '24h'; } });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/metrics/traffic', { params: { range } });
      setData(r.data); setError(''); setLoadedAt(new Date());
    } catch (e) { setError(e?.response?.data?.error || t('traffic.loadFailed', "Couldn't load the API traffic.")); }
  }, [range, t]);

  // reload every 30 s while the tab is visible (the server writes its counters every 30 s)
  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const pick = (r) => { setRange(r); try { localStorage.setItem('fluxbot_traffic_range', r); } catch (_) { } };

  const names = {
    panel: t('traffic.srcPanel', 'Panel (signed in)'),
    api_key: t('traffic.srcApiKey', 'API keys'),
    public: t('traffic.srcPublic', 'Public pages'),
    game_logs: t('traffic.srcGameLogs', 'Game-server logs'),
    errors_4xx: t('traffic.err4xx', 'Client errors (4xx)'),
    errors_5xx: t('traffic.err5xx', 'Server errors (5xx)'),
    total: t('traffic.total', 'Total'),
  };
  const rangeName = { '1h': t('traffic.range1h', '1 hour'), '24h': t('traffic.range24h', '24 hours'), '7d': t('traffic.range7d', '7 days'), '30d': t('traffic.range30d', '30 days') };
  const bucketName = (m) => (m >= 60 ? t('traffic.bucketHours', '{{n}} h per column', { n: m / 60 }) : t('traffic.bucketMinutes', '{{n}} min per column', { n: m }));

  const tot = data && data.totals;
  const minutesInRange = { '1h': 60, '24h': 1440, '7d': 10080, '30d': 43200 }[data ? data.range : range];
  const errCount = tot ? tot.errors_4xx + tot.errors_5xx : 0;

  return (
    <div className="tfc">
      <div className="tfc-bar">
        <div className="tfc-seg" role="group" aria-label={t('traffic.range', 'Time range')}>
          {RANGES.map(r => <button key={r} type="button" className={range === r ? 'on' : ''} aria-pressed={range === r} onClick={() => pick(r)}>{rangeName[r]}</button>)}
        </div>
        {loadedAt && <span className="tfc-updated">{t('traffic.updated', 'Updated {{time}}', { time: loadedAt.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })}</span>}
      </div>

      {error && <div className="tfc-msg bad" role="alert">{error}</div>}
      {!data && !error && <div className="tfc-msg">{t('common.loading', 'Loading...')}</div>}

      {data && (
        <>
          <div className="tfc-figs">
            <div className="tfc-fig"><b>{fmtNum(tot.requests, lang)}</b><span>{t('traffic.requests', 'Requests')}</span></div>
            <div className="tfc-fig"><b>{(tot.requests / minutesInRange).toLocaleString(lang, { maximumFractionDigits: tot.requests / minutesInRange < 1 ? 2 : 1 })}</b><span>{t('traffic.perMinute', 'Per minute, average')}</span></div>
            <div className="tfc-fig">
              <b>{tot.peak_per_minute ? fmtNum(tot.peak_per_minute.requests, lang) : '—'}</b><span>{t('traffic.peak', 'Busiest minute')}</span>
              {tot.peak_per_minute && <small>{new Date(tot.peak_per_minute.at).toLocaleString(lang, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</small>}
            </div>
            <div className="tfc-fig">
              <b>{fmtNum(errCount, lang)}</b><span>{t('traffic.errors', 'Errors')}</span>
              <small>{tot.requests ? t('traffic.errorShare', '{{pct}}% of requests', { pct: ((errCount / tot.requests) * 100).toFixed(1) }) : '—'}</small>
            </div>
            <div className="tfc-fig"><b>{tot.avg_ms == null ? '—' : `${fmtNum(tot.avg_ms, lang)} ms`}</b><span>{t('traffic.avgResponse', 'Average response time')}</span></div>
          </div>

          <div className="tfc-chart">
            <div className="tfc-chart-head">
              <h4>{t('traffic.requestsBySource', 'Requests by source')}</h4>
              <ul className="tfc-legend">
                {SOURCES.map(s => <li key={s} style={{ '--c': COLOR[s] }}><i />{names[s]} <b>{fmtNum(tot.by_source[s], lang)}</b></li>)}
              </ul>
              <span className="tfc-updated">{bucketName(data.bucket_minutes)}</span>
            </div>
            <StackedColumns series={data.series} keys={SOURCES} bucketMin={data.bucket_minutes} names={names} label={t('traffic.requestsBySource', 'Requests by source')} />
          </div>

          <div className="tfc-chart">
            <div className="tfc-chart-head">
              <h4>{t('traffic.errors', 'Errors')}</h4>
              <ul className="tfc-legend">
                {ERRORS.map(s => <li key={s} style={{ '--c': COLOR[s] }}><i />{names[s]} <b>{fmtNum(tot[s], lang)}</b></li>)}
              </ul>
            </div>
            <StackedColumns series={data.series} keys={ERRORS} bucketMin={data.bucket_minutes} names={names} label={t('traffic.errors', 'Errors')} />
          </div>

          <div className="tfc-chart">
            <div className="tfc-chart-head"><h4>{t('traffic.avgResponse', 'Average response time')}</h4></div>
            <LineChart series={data.series} field="avg_ms" unit=" ms" bucketMin={data.bucket_minutes} name={t('traffic.avgResponse', 'Average response time')} label={t('traffic.avgResponse', 'Average response time')} />
          </div>

          <details className="tfc-details">
            <summary>{t('traffic.showTable', 'Show the numbers as a table')}</summary>
            <div className="tfc-scroll">
              <table className="tfc-table">
                <thead><tr>
                  <th>{t('traffic.time', 'Time')}</th>
                  {SOURCES.map(s => <th key={s} className="num">{names[s]}</th>)}
                  <th className="num">4xx</th><th className="num">5xx</th><th className="num">ms</th>
                </tr></thead>
                <tbody>
                  {[...data.series].reverse().map(b => (
                    <tr key={b.t}>
                      <td>{timeLabel(b.t, data.bucket_minutes, lang, true)}</td>
                      {SOURCES.map(s => <td key={s} className="num">{fmtNum(b[s], lang)}</td>)}
                      <td className="num">{fmtNum(b.errors_4xx, lang)}</td><td className="num">{fmtNum(b.errors_5xx, lang)}</td>
                      <td className="num">{b.avg_ms == null ? '—' : fmtNum(b.avg_ms, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <div className="tfc-cols">
            <div>
              <h4>{t('traffic.topEndpoints', 'Busiest endpoints')}</h4>
              {data.endpoints.length === 0 ? <div className="tfc-empty">{t('traffic.none', 'Nothing in this range yet.')}</div> : (
                <table className="tfc-table">
                  <thead><tr>
                    <th>{t('traffic.endpoint', 'Endpoint')}</th>
                    <th className="num">{t('traffic.requests', 'Requests')}</th>
                    <th className="num">{t('traffic.errors', 'Errors')}</th>
                    <th className="num">{t('traffic.avgMs', 'Avg ms')}</th>
                  </tr></thead>
                  <tbody>
                    {data.endpoints.map(e => (
                      <tr key={`${e.method} ${e.route}`}>
                        <td className="mono"><span className="muted">{e.method}</span> {e.route === '(not found)' ? t('traffic.notFound', 'Not found (404)') : e.route}</td>
                        <td className="num">{fmtNum(e.requests, lang)}</td>
                        <td className={`num${e.errors ? ' bad' : ' muted'}`}>{fmtNum(e.errors, lang)}</td>
                        <td className="num">{e.avg_ms == null ? '—' : fmtNum(e.avg_ms, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <h4>{t('traffic.topKeys', 'API keys')}</h4>
              {data.api_keys.length === 0 ? <div className="tfc-empty">{t('traffic.noKeys', 'No API key requests in this range.')}</div> : (
                <table className="tfc-table">
                  <thead><tr>
                    <th>{t('traffic.key', 'Key')}</th>
                    <th className="num">{t('traffic.requests', 'Requests')}</th>
                    <th className="num">{t('traffic.errors', 'Errors')}</th>
                  </tr></thead>
                  <tbody>
                    {data.api_keys.map(k => (
                      <tr key={k.key_id}>
                        <td>
                          {k.name || t('traffic.deletedKey', 'Deleted key')}
                          {k.revoked && <span className="bad"> {t('traffic.revoked', '(revoked)')}</span>}
                          <div className="mono muted">{k.prefix ? `${k.prefix}…` : ''} {k.owner || ''}</div>
                        </td>
                        <td className="num">{fmtNum(k.requests, lang)}</td>
                        <td className={`num${k.errors ? ' bad' : ' muted'}`}>{fmtNum(k.errors, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
